/**
 * 语音过滤工具
 * 提供前端语音数据过滤功能，支持语种、方言、标签、关键词等多维度过滤
 */

import type { VoiceResponse } from '@/api/voice';

// 语言类型枚举
export enum LanguageType {
  CHINESE = 'zh',
  ENGLISH = 'en', 
  OTHER = 'other'
}

// 语言类型映射
export const LANGUAGE_TYPE_MAP = {
  '1': LanguageType.CHINESE,
  '2': LanguageType.ENGLISH,
  '3': LanguageType.OTHER
} as const;

// 语音过滤器类
export class VoiceFilter {
  private allVoices: VoiceResponse[] = [];
  private dialectCache: Array<{label: string, value: string}> | null = null;
  private countryCache: Array<{label: string, value: string}> | null = null;
  private lastVoicesHash: string = '';

  constructor(voices: VoiceResponse[] = []) {
    this.allVoices = voices;
    this.updateCacheHash();
  }

  /**
   * 设置所有语音数据
   */
  setVoices(voices: VoiceResponse[]) {
    this.allVoices = voices;
    this.updateCacheHash();
    this.clearCache();
  }

  /**
   * 更新缓存哈希值
   */
  private updateCacheHash() {
    this.lastVoicesHash = JSON.stringify(this.allVoices.map(v => ({ id: v.id, dialect: v.dialect, locale: v.locale, label: v.label })));
  }

  /**
   * 清除缓存
   */
  private clearCache() {
    this.dialectCache = null;
    this.countryCache = null;
  }

  /**
   * 检查缓存是否有效
   */
  private isCacheValid(): boolean {
    const currentHash = JSON.stringify(this.allVoices.map(v => ({ id: v.id, dialect: v.dialect, locale: v.locale, label: v.label })));
    return currentHash === this.lastVoicesHash;
  }

  /**
   * 获取当前数据中的所有方言类型（从后端数据动态提取，带缓存）
   */
  getAvailableDialects(): Array<{label: string, value: string}> {
    // 检查缓存
    if (this.dialectCache && this.isCacheValid()) {
      return this.dialectCache;
    }

    const dialectMap = new Map<string, string>();

    this.allVoices.forEach(voice => {
      if (voice.dialect && voice.label) {
        // 从后端数据中提取方言信息
        dialectMap.set(voice.dialect, voice.label);
      }
    });

    const options: Array<{label: string, value: string}> = [];
    dialectMap.forEach((label, dialect) => {
      options.push({ label, value: dialect });
    });

    // 按标签名称排序
    const result = options.sort((a, b) => a.label.localeCompare(b.label));

    // 缓存结果
    this.dialectCache = result;
    return result;
  }

  /**
   * 获取当前数据中的所有国家标签（按国家聚合并显示主播数量，带缓存）
   */
  getAvailableCountries(): Array<{label: string, value: string}> {
    // 检查缓存
    if (this.countryCache && this.isCacheValid()) {
      return this.countryCache;
    }

    const countryMap = new Map<string, {label: string, count: number, locales: string[]}>();

    this.allVoices.forEach(voice => {
      if (voice.label && voice.locale) {
        // 提取国家名称（去掉语言前缀）
        const countryName = this.extractCountryName(voice.label);

        if (countryMap.has(countryName)) {
          const existing = countryMap.get(countryName)!;
          existing.count++;
          if (!existing.locales.includes(voice.locale)) {
            existing.locales.push(voice.locale);
          }
        } else {
          countryMap.set(countryName, {
            label: voice.label,
            count: 1,
            locales: [voice.locale]
          });
        }
      }
    });

    const options: Array<{label: string, value: string}> = [];
    countryMap.forEach((info, countryName) => {
      // 显示格式：国家名称 (主播数量)
      const displayLabel = `${countryName} (${info.count})`;
      // value 使用国家名称，这样过滤时更准确
      options.push({
        label: displayLabel,
        value: countryName // 直接使用国家名称作为值
      });
    });

    // 按国家名称排序
    const result = options.sort((a, b) => {
      const nameA = a.label.split(' (')[0];
      const nameB = b.label.split(' (')[0];
      return nameA.localeCompare(nameB);
    });

    // 缓存结果
    this.countryCache = result;
    return result;
  }

  /**
   * 从完整标签中提取国家名称
   * 例如：'英语(美国)' -> '美国', '阿拉伯语(沙特阿拉伯)' -> '沙特阿拉伯'
   */
  private extractCountryName(label: string): string {
    const match = label.match(/\(([^)]+)\)$/);
    return match ? match[1] : label;
  }

  /**
   * 根据方言过滤语音
   */
  filterByDialect(voices: VoiceResponse[], dialect: string): VoiceResponse[] {
    if (!dialect) return voices;

    return voices.filter(voice => voice.dialect === dialect);
  }

  /**
   * 根据国家/地区过滤其他语言语音（支持按国家聚合过滤）
   */
  filterByLocale(voices: VoiceResponse[], localeOrCountry: string): VoiceResponse[] {
    if (!localeOrCountry) return voices;

    // 首先尝试按国家名称匹配（用于聚合后的过滤）
    return voices.filter(voice => {
      if (voice.label) {
        const voiceCountry = this.extractCountryName(voice.label);
        // 支持国家名称匹配
        if (voiceCountry === localeOrCountry) {
          return true;
        }
      }

      // 如果国家名称不匹配，尝试直接locale匹配
      return voice.locale === localeOrCountry;
    });
  }

  /**
   * 根据标签过滤语音（使用并集逻辑 - OR）
   */
  filterByTags(voices: VoiceResponse[], selectedTags: string[]): VoiceResponse[] {
    if (!selectedTags || selectedTags.length === 0) return voices;

    // 改为并集逻辑：只要包含任意一个选中的标签就符合条件
    return voices.filter(voice =>
      selectedTags.some(tag => voice.tags?.includes(tag))
    );
  }

  /**
   * 根据关键词过滤语音
   */
  filterByKeyword(voices: VoiceResponse[], keyword: string): VoiceResponse[] {
    if (!keyword) return voices;
    
    const lowerKeyword = keyword.toLowerCase();
    return voices.filter(voice => 
      voice.name.toLowerCase().includes(lowerKeyword) ||
      voice.localName?.toLowerCase().includes(lowerKeyword) ||
      voice.description?.toLowerCase().includes(lowerKeyword)
    );
  }

  /**
   * 获取指定语言类型的可用方言/国家选项
   */
  getAvailableOptions(type: LanguageType): Array<{label: string, value: string}> {
    switch (type) {
      case LanguageType.CHINESE:
        return this.getAvailableDialects();
      case LanguageType.OTHER:
        return this.getAvailableCountries();
      default:
        return [];
    }
  }

  /**
   * 获取指定语音列表的所有标签
   */
  getAvailableTags(voices: VoiceResponse[]): string[] {
    const tagSet = new Set<string>();
    voices.forEach(voice => {
      voice.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }

  /**
   * 综合过滤方法
   */
  filter(options: {
    dialect?: string;
    locale?: string;
    tags?: string[];
    keyword?: string;
  }): VoiceResponse[] {
    let result = this.allVoices;

    // 按方言过滤
    if (options.dialect) {
      result = this.filterByDialect(result, options.dialect);
    }

    // 按国家/地区过滤
    if (options.locale) {
      result = this.filterByLocale(result, options.locale);
    }

    // 按标签过滤
    if (options.tags && options.tags.length > 0) {
      result = this.filterByTags(result, options.tags);
    }

    // 按关键词过滤
    if (options.keyword) {
      result = this.filterByKeyword(result, options.keyword);
    }

    return result;
  }
}

// 创建默认过滤器实例
export const voiceFilter = new VoiceFilter();